import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Gravatar from 'react-gravatar';
import { isEmpty } from 'lodash';

import { Box, Modal, Button, Grid, Typography, CircularProgress } from '@mui/material';
import { Close, ChatOutlined, ErrorOutline, Send } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

import { Colors } from '../config/default';
import { getDocumentDetails } from '../services/services';
import { documentCommentsText } from '../constants/appConstants';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: Colors.Canvas_BG,
  borderRadius: '20px',
  p: 3
};

export default function DocumentComments({ id, comment, setComment }) {
  const scrollContainerRef = useRef(null);
  const navigate=useNavigate();
  const { COMMENTS, EMPTY_COMMENTS } = documentCommentsText;
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const [open, setOpen] = React.useState(false);
  const [commentArray, setCommentArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState('');

  const handleClose = () => setOpen(false);
  const selectedData = async () => {
    setLoading(true);
    const result = await getDocumentDetails(id);
    if (result?.response?.status === 401) {
      localStorage.clear();
      navigate('/');
    }
    const res = result?.data?.payload?.data?.document?.comment || [];
    setCommentArray((prevComments) => [...res, ...comment]);
    setLoading(false);
  };

  const handleOpen = (event) => {
    event.stopPropagation();
    setOpen(true);
    selectedData();
  };
  const handleInputChange = (e) => {
    setInputVal(e.target.value);
  };
  const handleSendClick = () => {
    const trimmedInput = inputVal?.trim();
    if (trimmedInput !== '' && inputVal !== '') {
      const newComment = {
        comment: inputVal,
        time: Date.now(),
        userName: userDetails?.user?.userName,
        email: userDetails?.user?.email,
        role: userDetails?.role
      };
      setComment((prevComments) => [...prevComments, newComment]);
      const mergedComments = [...commentArray, newComment];
      setCommentArray(mergedComments);
      setInputVal('');
    }
  };
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [commentArray]);
  return (
    <div>
      <Button
        variant="outlined"
        onClick={(e) => handleOpen(e)}
        sx={{
          fontSize: '.8em',
          textTransform: 'none',
          borderRadius: '10px',
          width: '8rem',
          height: '2rem',
          color: Colors.WHITE,
          marginRight: '1rem',
          '&:disabled': {
            color: 'gray',
            cursor: 'not-allowed'
          },
          '&:hover': {
            background: 'none'
          }
        }}
        startIcon={
          <ChatOutlined
            sx={{
              color: Colors.DARKBLUE
            }}
          />
        }>
        {COMMENTS}
      </Button>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          sx={{
            ...style,
            width: { xs: '70vw', lg: '35vw' }
          }}>
          <Grid
            container
            sx={{
              alignItems: 'center',
              marginBottom: '.5em'
            }}>
            <Grid item xs={10.5}>
              <Typography
                sx={{
                  color: Colors.WHITE,
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  paddingLeft: { sm: '3.5em', xs: '1.5em' }
                }}>
                {COMMENTS}
              </Typography>
            </Grid>
            <Grid
              item
              xs={1.5}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <Close
                sx={{ fontSize: '1.5rem', color: Colors.LIGHTGREY, cursor: 'pointer' }}
                onClick={handleClose}
              />
            </Grid>
          </Grid>
          <Grid
            item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '60vh',
              overflowY: 'scroll',
              '&::-webkit-scrollbar': {
                width: '.1em',
                height: '0em',
                borderRadius: '20px',
                scrollbarWidth: 'overlay',
                scrollbarColor: '#fff transparent'
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.11)',
                webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.11)',
                outline: '1px solid slategrey'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#fff',
                outline: '1px solid slategrey'
              }
            }}
            ref={scrollContainerRef}>
            {loading ? (
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <CircularProgress size={22} sx={{ color: Colors.DARKBLUE }} />
              </Grid>
            ) : isEmpty(commentArray) ? (
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Box
                  sx={{
                    backgroundColor: Colors.NAV_BLACK,
                    padding: '.5em',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '.8em',
                    color: Colors?.TEXTGREY
                  }}>
                  <ErrorOutline
                    sx={{ color: Colors.TEXTGREY, marginRight: '.3em', fontSize: '1rem' }}
                  />
                  {EMPTY_COMMENTS}
                </Box>
              </Grid>
            ) : (
              commentArray?.map((comment) => {
                return (
                  <Grid item sx={{ display: 'flex', marginTop: '1rem' }}>
                    <Gravatar
                      email={comment?.email}
                      style={{
                        marginTop: '.1em',
                        order: '2px solid #1AACAC',
                        height: '2.5em',
                        width: '2.5em',
                        borderRadius: '50%'
                      }}
                    />
                    <Grid
                      item
                      sx={{
                        height: 'max-content',
                        marginLeft: '.5em',
                        p: '.3rem .6rem',
                        borderRadius: '10px',
                        backgroundColor: Colors.NAV_BLACK,
                        maxWidth: '60%',
                        minWidth: '30%'
                      }}>
                      <Typography
                        sx={{ fontSize: '.9rem', color: Colors.WHITE, marginBottom: '.1rem' }}>
                        {comment?.userName}
                      </Typography>
                      <Typography sx={{ fontSize: '.8rem', color: Colors.TEXTGREY }}>
                        {comment?.comment}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '.6rem',
                          color: Colors.TEXTGREY,
                          float: 'right',
                          mt: '.1em'
                        }}>
                        {comment?.time &&
                          new Date(comment?.time).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })
            )}
          </Grid>
          <Grid container>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '1em' }}>
              <input
                type="text"
                placeholder="Type Here"
                onChange={handleInputChange}
                value={inputVal}
                style={{
                  width: '-webkit-fill-available',
                  height: '3em',
                  outline: 'none',
                  paddingLeft: '2em',
                  paddingRight: '5em',
                  color: Colors.WHITE,
                  backgroundColor: Colors.NAV_BLACK,
                  border: 'none',
                  borderRadius: '0.5em',
                  position: 'relative'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                    e.preventDefault();
                    handleSendClick();
                  }
                }}
              />
              <IconButton
                disabled={inputVal === '' || inputVal?.trim() === ''}
                onClick={handleSendClick}
                aria-label="Send"
                sx={{
                  color: Colors.DARKBLUE,
                  fontSize: '1.5em',
                  position: 'absolute',
                  backgroundColor: Colors.NAV_BLACK,
                  right: '40px',
                  '&:disabled': {
                    color: 'gray',
                    cursor: 'not-allowed'
                  }
                }}>
                <Send />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}
